import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { ENDPOINTS } from '../config';
import { isCartFinal, type CartChargeStatus, type CartStatus } from '../utils/cart';
import { formatDateInBusinessTZ } from '../utils/date';
import { checkoutPath } from '../utils/checkoutSource';
import settings from '../config/settings.json';

interface CartView {
  id: string;
  kind: 'card' | 'ach' | 'zelle';
  status: CartStatus;
  charge_status: CartChargeStatus;
  fee_cents: number;
  /** Zelle carts only. */
  memoCode: string | null;
  expiresAt: string;
  items: Array<{
    invoiceId: string;
    studentName: string;
    programName: string;
    paidCents: number;
    snapshotCents: number;
    /** What this invoice still owes now. */
    owedCents: number;
  }>;
  /** What the invoices still owe now. */
  owedCents: number;
  /** Everything that has arrived against the cart, across every send. */
  fundedCents: number;
  walletCreditCents: number;
}

interface RawCart {
  id: string;
  kind: CartView['kind'];
  status: CartView['status'];
  charge_status: CartView['charge_status'];
  fee_cents: number;
  memo_code: string | null;
  expires_at: string;
  payment_cart_items: Array<{
    invoice_id: string;
    snapshot_balance_cents: number;
    sort_order: number;
    invoices: {
      total_amount: number;
      amount_paid: number;
      status: string;
      enrollments: {
        students: { first_name: string; last_name: string } | null;
        programs: { name: string } | null;
      } | null;
    } | null;
  }>;
  payments: Array<{ invoice_id: string; amount: number }>;
  payment_cart_fundings: Array<{ amount_cents: number; residue_cents: number }>;
}

/** How long the page keeps asking before it tells the parent to check back. */
const POLL_MS = 2000;
const POLL_LIMIT = 15;

function usd(cents: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

/**
 * Where a bundle payment lands after the browser confirms it. Asks cart-checkout to
 * bring the cart in line with Stripe (which records the money if the webhook has not
 * yet), then shows the cart as the database has it.
 */
export function CartConfirmation() {
  const { cartId } = useParams();
  const { user, isLoading: isAuthLoading } = useAuth();
  const queryClient = useQueryClient();
  const [polls, setPolls] = useState(0);
  const finalized = useRef(false);

  // Once per visit: settle from Stripe's side if the webhook has not got there yet.
  useEffect(() => {
    if (isAuthLoading || !user || !cartId || finalized.current) return;
    finalized.current = true;
    (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;
        if (!token) return;
        await fetch(ENDPOINTS.cartCheckout, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: 'finalize', cart_id: cartId }),
        });
      } catch {
        // The webhook settles it regardless; polling below shows whichever lands.
      }
    })();
  }, [isAuthLoading, user, cartId]);

  const { data: cart, error } = useQuery<CartView | null>({
    queryKey: ['cart-confirmation', cartId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('payment_carts')
        .select(`
          id, kind, status, charge_status, fee_cents, memo_code, expires_at,
          payment_cart_items ( invoice_id, snapshot_balance_cents, sort_order,
            invoices ( total_amount, amount_paid, status, enrollments ( students ( first_name, last_name ), programs ( name ) ) ) ),
          payments ( invoice_id, amount ),
          payment_cart_fundings ( amount_cents, residue_cents )
        `)
        .eq('id', cartId!)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const raw = data as unknown as RawCart;
      const paid = new Map<string, number>();
      for (const p of raw.payments ?? []) {
        paid.set(p.invoice_id, (paid.get(p.invoice_id) ?? 0) + Math.round(Number(p.amount) * 100));
      }
      return {
        id: raw.id,
        kind: raw.kind,
        status: raw.status,
        charge_status: raw.charge_status,
        fee_cents: raw.fee_cents,
        memoCode: raw.memo_code,
        expiresAt: raw.expires_at,
        owedCents: (raw.payment_cart_items ?? []).reduce((sum, item) => {
          const inv = item.invoices;
          if (!inv || inv.status === 'voided') return sum;
          return sum + Math.max(0, Math.round(Number(inv.total_amount) * 100) - Math.round(Number(inv.amount_paid) * 100));
        }, 0),
        fundedCents: (raw.payment_cart_fundings ?? []).reduce((sum, f) => sum + f.amount_cents, 0),
        items: [...(raw.payment_cart_items ?? [])]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((item) => {
            const student = item.invoices?.enrollments?.students;
            const inv = item.invoices;
            return {
              invoiceId: item.invoice_id,
              studentName: student ? `${student.first_name} ${student.last_name}` : 'Student',
              programName: item.invoices?.enrollments?.programs?.name ?? 'Program',
              paidCents: paid.get(item.invoice_id) ?? 0,
              snapshotCents: item.snapshot_balance_cents,
              owedCents: !inv || inv.status === 'voided'
                ? 0
                : Math.max(0, Math.round(Number(inv.total_amount) * 100) - Math.round(Number(inv.amount_paid) * 100)),
            };
          }),
        walletCreditCents: (raw.payment_cart_fundings ?? []).reduce((sum, f) => sum + f.residue_cents, 0),
      };
    },
    enabled: !isAuthLoading && !!user && !!cartId,
    refetchInterval: (query) => {
      const current = query.state.data;
      // A Zelle transfer arrives in minutes to hours, not seconds; the page refreshes
      // when the parent comes back to it instead.
      if (current && (current.kind === 'zelle' || isCartFinal(current))) return false;
      return polls < POLL_LIMIT ? POLL_MS : false;
    },
  });

  useEffect(() => {
    if (cart && cart.kind !== 'zelle' && !isCartFinal(cart)) setPolls((n) => n + 1);
  }, [cart]);

  // Everything that lists these invoices is out of date once the cart moves.
  useEffect(() => {
    if (!cart || !isCartFinal(cart)) return;
    void queryClient.invalidateQueries({ queryKey: ['invoices-list'] });
    void queryClient.invalidateQueries({ queryKey: ['payments-list'] });
    void queryClient.invalidateQueries({ queryKey: ['cart-held-invoices'] });
  }, [cart, queryClient]);

  const back = (
    <Link to="/payments" className="inline-block mt-6 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">
      Back to Payments
    </Link>
  );

  if (error || cart === null) {
    return (
      <div className="p-4">
        <div className="max-w-2xl mx-auto bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded" role="alert">
          We couldn&apos;t find this payment. Check your Payments page for its status.
        </div>
        <div className="max-w-2xl mx-auto">{back}</div>
      </div>
    );
  }

  if (!cart) {
    return (
      <div className="p-4" data-testid="loading-skeleton">
        <div className="max-w-2xl mx-auto animate-pulse h-40 bg-gray-200 rounded" />
      </div>
    );
  }

  if (cart.kind === 'zelle' && cart.status !== 'settled') {
    return (
      <div className="p-4">
        <div className="max-w-2xl mx-auto">
          <ZelleCartPanel cart={cart} />
          {back}
        </div>
      </div>
    );
  }

  const failed = cart.charge_status === 'failed' || cart.status === 'cancelled';
  const processing = !failed && cart.status !== 'settled' && cart.charge_status === 'processing';
  const settled = cart.status === 'settled' || cart.status === 'partially_funded';
  const totalPaidCents = cart.items.reduce((sum, i) => sum + i.paidCents, 0);

  return (
    <div className="p-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6" data-testid="cart-confirmation">
          {failed ? (
            <>
              <h1 className="text-xl font-semibold text-gray-900">This payment didn&apos;t go through</h1>
              <p className="mt-2 text-sm text-gray-600">Nothing was charged. Your invoices are still open and can be paid again.</p>
            </>
          ) : settled ? (
            <>
              <h1 className="text-xl font-semibold text-gray-900">
                Paid {cart.items.length} invoice{cart.items.length === 1 ? '' : 's'}
              </h1>
              <p className="mt-2 text-sm text-gray-600">A receipt for the whole payment is on its way by email.</p>
            </>
          ) : processing ? (
            <>
              <h1 className="text-xl font-semibold text-gray-900">Your bank payment is processing</h1>
              <p className="mt-2 text-sm text-gray-600">
                ACH payments usually take 3–5 business days. These invoices show as processing until it clears, and
                you&apos;ll get a receipt by email then.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-xl font-semibold text-gray-900">Confirming your payment…</h1>
              <p className="mt-2 text-sm text-gray-600">
                {polls >= POLL_LIMIT
                  ? 'This is taking longer than usual. Your Payments page will show it as soon as it is confirmed.'
                  : 'This only takes a moment.'}
              </p>
            </>
          )}

          <CartInvoiceRows
            items={cart.items}
            amount={(item) => (
              <span className="font-semibold text-gray-900">{usd(settled ? item.paidCents : item.snapshotCents)}</span>
            )}
          />

          {failed && (
            <Link to={payAgainPath(cart.items.map((i) => i.invoiceId))} className="inline-block mt-4 text-sm font-medium text-blue-600 hover:underline" data-testid="cart-pay-again">
              Pay these invoices again
            </Link>
          )}

          {settled && (
            <div className="mt-4 space-y-1 text-sm">
              {cart.fee_cents > 0 && (
                <p className="text-gray-500">Includes a {usd(cart.fee_cents)} card processing fee, shared across the invoices.</p>
              )}
              <p className="font-semibold text-gray-900">Total paid: {usd(totalPaidCents + cart.walletCreditCents)}</p>
              {cart.walletCreditCents > 0 && (
                <p className="text-green-700">
                  {usd(cart.walletCreditCents)} more than these invoices owed was added to your{' '}
                  <Link to="/wallet" className="underline">BHCS Cash</Link>.
                </p>
              )}
            </div>
          )}
        </div>
        {back}
      </div>
    </div>
  );
}

/** Each invoice in the cart as one row that opens the invoice. */
function CartInvoiceRows({ items, amount }: { items: CartView['items']; amount: (item: CartView['items'][number]) => ReactNode }) {
  return (
    <ul className="mt-6 divide-y divide-gray-100 border-t border-gray-100">
      {items.map((item) => (
        <li key={item.invoiceId}>
          <Link
            to={`/invoices/${item.invoiceId}`}
            className="flex items-center justify-between gap-4 py-3 -mx-2 px-2 rounded-lg hover:bg-gray-50"
            aria-label={`View invoice: ${item.studentName}, ${item.programName}`}
            data-testid={`cart-item-${item.invoiceId}`}
          >
            <span className="min-w-0">
              <span className="block font-medium text-gray-900">{item.studentName}</span>
              <span className="block text-sm text-gray-600">{item.programName}</span>
            </span>
            <span className="flex items-center gap-2 shrink-0">
              {amount(item)}
              <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/**
 * Where to pay these invoices again: the cart with them ticked (anything since paid simply
 * isn't listed), or -- one invoice is never a bundle -- that invoice's own checkout.
 */
function payAgainPath(invoiceIds: string[]): string {
  if (invoiceIds.length === 1) return checkoutPath(invoiceIds[0], 'due_now');
  return `/cart?invoices=${invoiceIds.join(',')}`;
}

function PayAgainLink({ cart }: { cart: CartView }) {
  const stillOwed = cart.items.filter((i) => i.owedCents > 0).map((i) => i.invoiceId);
  if (stillOwed.length === 0) return null;
  return (
    <Link to={payAgainPath(stillOwed)} className="inline-block mt-3 text-sm font-medium text-blue-600 hover:underline" data-testid="cart-pay-again">
      Choose these invoices again
    </Link>
  );
}

function CopyValue({ label, value, testId }: { label: string; value: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-sm text-gray-700 font-medium w-24">{label}</span>
      <button
        type="button"
        data-testid={testId}
        className="font-mono font-bold text-lg tracking-wide bg-yellow-50 border border-yellow-200 text-yellow-900 px-3 py-0.5 rounded-full hover:bg-yellow-100"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch {
            // No clipboard: the value is on screen to copy by hand.
          }
        }}
      >
        {value}
      </button>
      <span className="text-xs text-gray-500">{copied ? '✓ Copied' : 'tap to copy'}</span>
    </div>
  );
}

/**
 * A Zelle cart: the one code and amount to send, how much has arrived, and what to
 * do if the bank caps the transfer. Its money comes in through the Zelle importer,
 * so this only ever reflects what has been recorded so far.
 */
function ZelleCartPanel({ cart }: { cart: CartView }) {
  const live = (cart.status === 'open' || cart.status === 'partially_funded') && Date.parse(cart.expiresAt) > Date.now();
  const until = formatDateInBusinessTZ(cart.expiresAt);
  return (
    <div className="bg-white rounded-lg shadow p-6" data-testid="cart-confirmation">
      {live ? (
        <>
          <h1 className="text-xl font-semibold text-gray-900">
            Send {usd(cart.owedCents)} by Zelle
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            One transfer pays all {cart.items.length} invoice{cart.items.length === 1 ? '' : 's'}. Put only this code in the
            Zelle memo.
          </p>
          <div className="mt-4 space-y-3 bg-gray-50 rounded-lg p-4" data-testid="zelle-instructions">
            <CopyValue label="Send to" value={settings.supportEmail} testId="zelle-recipient" />
            <CopyValue label="Memo" value={cart.memoCode ?? ''} testId="zelle-memo" />
            <CopyValue label="Amount" value={(cart.owedCents / 100).toFixed(2)} testId="zelle-amount" />
          </div>
          {cart.fundedCents > 0 && (
            <p className="mt-4 text-sm text-green-700" data-testid="zelle-received">
              Received so far: {usd(cart.fundedCents)}. Send the rest under the same memo code.
            </p>
          )}
          <p className="mt-4 text-sm text-gray-600">
            If your bank limits how much you can send in a day, send it over several days with the same memo code. This code
            works until {until}. Anything you send beyond what the invoices owe is added to your BHCS Cash.
          </p>
        </>
      ) : cart.status === 'cancelled' ? (
        <>
          <h1 className="text-xl font-semibold text-gray-900">This Zelle code is no longer in use</h1>
          <p className="mt-2 text-sm text-gray-600">
            It was replaced by a newer payment for these invoices. To pay by Zelle, choose the invoices again for a new code.
          </p>
          <PayAgainLink cart={cart} />
        </>
      ) : (
        <>
          <h1 className="text-xl font-semibold text-gray-900">This Zelle code expired on {until}</h1>
          <p className="mt-2 text-sm text-gray-600">
            {cart.fundedCents > 0
              ? `The ${usd(cart.fundedCents)} that arrived is on your invoices. `
              : ''}
            To pay what is left, choose the invoices again.
          </p>
          <PayAgainLink cart={cart} />
        </>
      )}

      <CartInvoiceRows
        items={cart.items}
        amount={(item) => (
          item.owedCents === 0 ? (
            <span className="text-sm font-medium text-green-700">Paid</span>
          ) : (
            <span className="text-right">
              <span className="block font-semibold text-gray-900">{usd(item.owedCents)}</span>
              {item.paidCents > 0 && <span className="block text-xs text-green-700">{usd(item.paidCents)} received</span>}
            </span>
          )
        )}
      />
    </div>
  );
}
